import React from "react";
import SearchableSelect from "src/components/custom/SearchableSelect";
import {
  INPUT_CLASS,
  LABEL_CLASS,
  sanitizeNameLettersOnly,
  sanitizePhoneDigits,
} from "src/utils/mcc/studentHelper";
import { BATCH_YEARS, GENDER_OPTIONS } from "src/utils/constants";

/**
 * Shared fields for MCC Add / Edit student modals.
 */
export default function StudentFormFields({
  form,
  setForm,
  districtOptions,
  itinameOptions,
  tradenameOptions,
  industrynameOptions,
  courseAffiliationGroupName,
  showReadOnlyCredentials = false,
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={LABEL_CLASS}>Registration ID</label>
          <input
            className={INPUT_CLASS}
            value={form.registration_id}
            onChange={(e) =>
              setForm((p) => ({ ...p, registration_id: e.target.value }))
            }
            placeholder="Registration ID"
          />
        </div>
        <div>
          <label className={LABEL_CLASS}>Name</label>
          <input
            className={INPUT_CLASS}
            autoComplete="name"
            value={form.name}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                name: sanitizeNameLettersOnly(e.target.value),
              }))
            }
            placeholder="e.g. Ravi Kumar — letters only"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={LABEL_CLASS}>Phone Number</label>
          <input
            className={INPUT_CLASS}
            inputMode="numeric"
            autoComplete="tel"
            maxLength={10}
            value={form.phone_number}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                phone_number: sanitizePhoneDigits(e.target.value),
              }))
            }
            placeholder="Digits only, max 10"
          />
        </div>
        <div>
          <label className={LABEL_CLASS}>Date of Birth</label>
          <input
            type="date"
            className={INPUT_CLASS}
            value={form.date_of_birth}
            onChange={(e) =>
              setForm((p) => ({ ...p, date_of_birth: e.target.value }))
            }
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={LABEL_CLASS}>Father&apos;s Name</label>
          <input
            className={INPUT_CLASS}
            value={form.father_name}
            onChange={(e) =>
              setForm((p) => ({ ...p, father_name: e.target.value }))
            }
            placeholder="Father's name"
          />
        </div>
        <div>
          <label className={LABEL_CLASS}>Mother&apos;s Name</label>
          <input
            className={INPUT_CLASS}
            value={form.mother_name}
            onChange={(e) =>
              setForm((p) => ({ ...p, mother_name: e.target.value }))
            }
            placeholder="Mother's name"
          />
        </div>
      </div>

      {showReadOnlyCredentials && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLASS}>User name</label>
            <input
              className={`${INPUT_CLASS} bg-gray-100 text-gray-500 cursor-not-allowed`}
              value={form.username || ""}
              disabled
              readOnly
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Password</label>
            <input
              className={INPUT_CLASS}
              value={form.password || ""}
              onChange={(e) =>
                setForm((p) => ({ ...p, password: e.target.value }))
              }
            />
          </div>
        </div>
      )}

      <div>
        <label className={LABEL_CLASS}>City of ITI</label>
        <input
          className={INPUT_CLASS}
          value={form.city_of_ITI}
          onChange={(e) =>
            setForm((p) => ({ ...p, city_of_ITI: e.target.value }))
          }
          placeholder="City of ITI"
        />
      </div>

      <SearchableSelect
        label="District"
        placeholder="Select District"
        options={districtOptions}
        value={form.district}
        onChange={(v) =>
          setForm((p) => ({
            ...p,
            district: v,
            itiname: "",
            tradename: "",
            industryname: "",
          }))
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SearchableSelect
          label="ITI"
          placeholder="Select ITI"
          options={itinameOptions}
          value={form.itiname}
          onChange={(v) =>
            setForm((p) => ({
              ...p,
              itiname: v,
              tradename: "",
              industryname: "",
            }))
          }
        />
        <SearchableSelect
          label="Trade"
          placeholder="Select Trade"
          options={tradenameOptions}
          value={form.tradename}
          onChange={(v) =>
            setForm((p) => ({
              ...p,
              tradename: v,
              industryname: "",
            }))
          }
        />
      </div>

      <SearchableSelect
        label="Industry"
        placeholder="Select Industry"
        options={industrynameOptions}
        value={form.industryname}
        onChange={(v) =>
          setForm((p) => ({ ...p, industryname: v }))
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SearchableSelect
          label="Batch of Trade"
          placeholder="Select Batch"
          options={BATCH_YEARS}
          value={form.batch_of_trade}
          onChange={(v) =>
            setForm((p) => ({ ...p, batch_of_trade: v }))
          }
        />
        <SearchableSelect
          label="Gender"
          placeholder="Select Gender"
          options={GENDER_OPTIONS}
          value={form.gender}
          onChange={(v) => setForm((p) => ({ ...p, gender: v }))}
        />
      </div>

      <div className="w-full flex flex-col gap-2">
        <label className={LABEL_CLASS}>Course Affiliation</label>
        <div className="flex mt-1.5 text-gray-500 gap-2 custom-radio custom-radio-sm">
          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="radio"
              name={courseAffiliationGroupName}
              value="NCVT"
              checked={form.course_affiliation_ === "NCVT"}
              onChange={() =>
                setForm((p) => ({ ...p, course_affiliation_: "NCVT" }))
              }
            />{" "}
            NCVT
          </label>
          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="radio"
              name={courseAffiliationGroupName}
              value="SCVT"
              checked={form.course_affiliation_ === "SCVT"}
              onChange={() =>
                setForm((p) => ({ ...p, course_affiliation_: "SCVT" }))
              }
            />{" "}
            SCVT
          </label>
        </div>
      </div>
    </div>
  );
}
